/* eslint-disable @typescript-eslint/no-unsafe-argument */
import {
  type ConfigLoader,
  type GrowthBookSourceInitSettings,
  type GrowthBookSourceUpdateSettings,
  createEnvConfigLoader,
  createGrowthBookConfigLoader,
  createLocalJsonConfigLoader,
} from '../../lib/loader';
import type { ValidateFunction } from 'ajv';
import type { AvailableSchemaTypes } from '../configLoader.types';
import { getFrontendConfigs } from './configLoaderGetWindowProp';
import { validateFrontendConfiguration, validateGrowthBook } from '../..';
import { type FrontendConfigurationSchema } from '../../generated/frontend';
import { type GrowthBookSchema } from '../../generated/growthBook';

/**
 * Generic function for creating a ConfigLoader for client-side configs
 * @param getConfigFn Function that returns a config
 * @param validateFn  Function that validates the config
 * @returns ConfigLoader<SchemaType>
 *
 * @example
 * ```ts
 * type ConfigSchema = {
 *  foo: string,
 * }
 * const testObject ={
 *  config: {
 *   foo: 'bar',
 * }
 * const clientConfigLoader = createClientConfigLoader<ConfigSchema>(
 *  () => testObject.config,
 *  someValidateFunction,
 *  );
 * ```
 */
export const createClientConfigLoader = <
  SchemaType extends AvailableSchemaTypes,
>(
  getConfigFn: () => SchemaType | undefined,
  validateFn: ValidateFunction,
): ConfigLoader<SchemaType> => {
  const config = getConfigFn();
  if (config === undefined) {
    throw new Error(
      `No config was found on the window when running ${getConfigFn.name}`,
    );
  }
  return createLocalJsonConfigLoader<SchemaType>(config, validateFn);
};

/**
 * Creates a new client-side ConfigLoader for loading frontend configurations
 * @returns ConfigLoader<FrontendConfigurationSchema>
 */
export const createClientFrontendConfigLoader =
  (): ConfigLoader<FrontendConfigurationSchema> =>
    createClientConfigLoader<FrontendConfigurationSchema>(
      getFrontendConfigs,
      validateFrontendConfiguration,
    );

/**
 * Creates a new server-side ConfigLoader for loading frontend configurations
 * @returns ConfigLoader<FrontendConfigurationSchema>
 */
export const createServerFrontendConfigLoader =
  (): ConfigLoader<FrontendConfigurationSchema> =>
    createEnvConfigLoader<FrontendConfigurationSchema>(
      validateFrontendConfiguration,
    );

/**
 * Creates a new client-side ConfigLoader for loading growthbook configurations
 * @returns ConfigLoader<GrowthBookSchema>
 */
export const createClientGrowthBookConfigLoader = (): ConfigLoader<
  GrowthBookSchema,
  GrowthBookSourceInitSettings,
  GrowthBookSourceUpdateSettings
> => createGrowthBookConfigLoader<GrowthBookSchema>(validateGrowthBook);
